import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, ChevronUp, ChevronDown, FolderTree, Image as ImageIcon, Loader2 } from "lucide-react";

const LEVEL_LABEL = { 1: "Group", 2: "Category", 3: "Subcategory" };

/* Build the full path (root→leaf) of node ids for a given leaf id */
export function categoryPath(nodes, leafId) {
  const byId = {}; (nodes || []).forEach((n) => { byId[n.id] = n; });
  const path = []; let cur = byId[leafId]; let guard = 0;
  while (cur && guard < 6) { path.unshift(cur); cur = cur.parent_id ? byId[cur.parent_id] : null; guard += 1; }
  return path;
}
export function categoryLabel(nodes, leafId) {
  const p = categoryPath(nodes, leafId);
  return p.length ? p.map((n) => n.name).join(" › ") : "";
}
const childrenOf = (nodes, parentId) =>
  (nodes || []).filter((n) => (n.parent_id || null) === (parentId || null)).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

/* ---------------- Cascading picker used inside the product editor ---------------- */
export function CategoryPicker({ nodes, valueId, onChange, onManage }) {
  const path = categoryPath(nodes, valueId);
  const selAt = (lvl) => (path[lvl - 1] ? path[lvl - 1].id : "");
  const optsAt = (lvl) => childrenOf(nodes, lvl === 1 ? null : selAt(lvl - 1));

  const pick = (lvl, id) => {
    if (!id) { onChange(lvl === 1 ? null : selAt(lvl - 1) || null); }
    else onChange(id);
  };

  return (
    <div className="space-y-2" data-testid="category-picker">
      {[1, 2, 3].map((lvl) => {
        const opts = optsAt(lvl);
        if (lvl > 1 && !selAt(lvl - 1)) return null;
        if (lvl > 1 && !opts.length) return null;
        return (
          <div key={lvl}>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{LEVEL_LABEL[lvl]}</Label>
            <select
              className="mt-1 w-full h-9 rounded-none border border-input bg-background px-2 text-sm"
              value={selAt(lvl)}
              onChange={(e) => pick(lvl, e.target.value)}
              data-testid={`category-picker-level-${lvl}`}
            >
              <option value="">{lvl === 1 ? "— Unassigned —" : "— None —"}</option>
              {opts.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
            </select>
          </div>
        );
      })}
      <button type="button" onClick={onManage} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="open-category-manager-btn">
        <FolderTree className="h-3 w-3" /> Manage categories
      </button>
      {valueId && <div className="text-[11px] text-muted-foreground">Path: {categoryLabel(nodes, valueId)}</div>}
    </div>
  );
}

/* ---------------- Tree manager dialog ---------------- */
export function CategoryTreeManager({ open, onOpenChange, nodes, reload }) {
  const [addUnder, setAddUnder] = useState(undefined); // undefined=none, null=root, id=child
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  const [heroFor, setHeroFor] = useState(null);
  const [heroImg, setHeroImg] = useState("");
  const [heroBlurb, setHeroBlurb] = useState("");
  const [heroBusy, setHeroBusy] = useState(false);

  const openHero = (n) => { setHeroFor(n.id); setHeroImg(n.image || ""); setHeroBlurb(n.blurb || ""); };
  const uploadHero = async (file) => {
    if (!file) return;
    setHeroBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await opApi.post("/tenant/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHeroImg(data.url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload failed"); } finally { setHeroBusy(false); }
  };
  const saveHero = async (id) => {
    setHeroBusy(true);
    try { await opApi.put(`/tenant/product-categories/${id}`, { image: heroImg, blurb: heroBlurb }); setHeroFor(null); await reload(); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setHeroBusy(false); }
  };

  const create = async (parentId) => {
    if (!newName.trim()) { toast.error("Enter a name"); return; }
    setBusy(true);
    try {
      await opApi.post("/tenant/product-categories", { name: newName.trim(), parent_id: parentId || null });
      setNewName(""); setAddUnder(undefined); await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const rename = async (id) => {
    if (!editName.trim()) return;
    setBusy(true);
    try { await opApi.put(`/tenant/product-categories/${id}`, { name: editName.trim() }); setEditId(null); await reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (n) => {
    if (!window.confirm(`Delete "${n.name}" and everything nested under it? Products here will move up to the parent.`)) return;
    setBusy(true);
    try { await opApi.delete(`/tenant/product-categories/${n.id}`); await reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const move = async (n, dir) => {
    const sibs = childrenOf(nodes, n.parent_id || null);
    const idx = sibs.findIndex((s) => s.id === n.id);
    const swap = sibs[idx + dir];
    if (!swap) return;
    setBusy(true);
    try {
      await Promise.all([
        opApi.put(`/tenant/product-categories/${n.id}`, { sort_order: swap.sort_order ?? (idx + dir) }),
        opApi.put(`/tenant/product-categories/${swap.id}`, { sort_order: n.sort_order ?? idx }),
      ]);
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const renderNode = (n, depth) => {
    const kids = childrenOf(nodes, n.id);
    const sibs = childrenOf(nodes, n.parent_id || null);
    const idx = sibs.findIndex((s) => s.id === n.id);
    return (
      <div key={n.id}>
        <div className="flex items-center gap-1 py-1 border-b border-border/60" style={{ paddingLeft: depth * 18 }} data-testid={`cat-node-${n.id}`}>
          {editId === n.id ? (
            <>
              <Input autoFocus className="rounded-none h-7 text-sm flex-1" value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") rename(n.id); if (e.key === "Escape") setEditId(null); }} data-testid={`cat-edit-input-${n.id}`} />
              <button className="text-emerald-600 p-1" onClick={() => rename(n.id)} data-testid={`cat-save-${n.id}`}><Check className="h-4 w-4" /></button>
              <button className="text-muted-foreground p-1" onClick={() => setEditId(null)}><X className="h-4 w-4" /></button>
            </>
          ) : (
            <>
              <span className="text-sm flex-1 truncate">{n.name} <span className="text-[10px] uppercase text-muted-foreground">{LEVEL_LABEL[n.level]}</span></span>
              <button className="text-muted-foreground hover:text-foreground p-1 disabled:opacity-20" disabled={idx <= 0} onClick={() => move(n, -1)} data-testid={`cat-up-${n.id}`}><ChevronUp className="h-3.5 w-3.5" /></button>
              <button className="text-muted-foreground hover:text-foreground p-1 disabled:opacity-20" disabled={idx >= sibs.length - 1} onClick={() => move(n, 1)} data-testid={`cat-down-${n.id}`}><ChevronDown className="h-3.5 w-3.5" /></button>
              {n.level < 3 && <button className="text-muted-foreground hover:text-primary p-1" onClick={() => { setAddUnder(n.id); setNewName(""); }} data-testid={`cat-add-child-${n.id}`}><Plus className="h-3.5 w-3.5" /></button>}
              <button className={`p-1 ${n.image || n.blurb ? "text-primary" : "text-muted-foreground hover:text-primary"}`} onClick={() => openHero(n)} data-testid={`cat-hero-${n.id}`} title="Hero image & blurb"><ImageIcon className="h-3.5 w-3.5" /></button>
              <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => { setEditId(n.id); setEditName(n.name); }} data-testid={`cat-rename-${n.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button className="text-muted-foreground hover:text-destructive p-1" onClick={() => del(n)} data-testid={`cat-del-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </>
          )}
        </div>
        {addUnder === n.id && (
          <div className="flex items-center gap-1 py-1" style={{ paddingLeft: (depth + 1) * 18 }} data-testid={`cat-add-row-${n.id}`}>
            <Input autoFocus className="rounded-none h-7 text-sm flex-1" placeholder={`New ${LEVEL_LABEL[(n.level || 1) + 1] || "item"}…`} value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") create(n.id); if (e.key === "Escape") setAddUnder(undefined); }} data-testid={`cat-add-input-${n.id}`} />
            <Button size="sm" className="rounded-none h-7" disabled={busy} onClick={() => create(n.id)} data-testid={`cat-add-confirm-${n.id}`}>Add</Button>
            <button className="text-muted-foreground p-1" onClick={() => setAddUnder(undefined)}><X className="h-4 w-4" /></button>
          </div>
        )}
        {heroFor === n.id && (
          <div className="py-2 bg-secondary/40 border-b border-border" style={{ paddingLeft: (depth + 1) * 18, paddingRight: 8 }} data-testid={`cat-hero-panel-${n.id}`}>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Storefront hero for “{n.name}” <span className="normal-case">(optional — leave blank to show a plain header)</span></div>
            {heroImg ? (
              <div className="relative mb-2 h-24 bg-cover bg-center border border-border" style={{ backgroundImage: `url('${heroImg}')` }}>
                <button className="absolute top-1 right-1 bg-white/90 p-1 text-destructive" onClick={() => setHeroImg("")} data-testid={`cat-hero-remove-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ) : null}
            <div className="flex items-center gap-2 mb-2">
              <label className="inline-flex items-center gap-1.5 text-xs border border-border px-2 py-1.5 cursor-pointer hover:bg-secondary" data-testid={`cat-hero-upload-${n.id}`}>
                {heroBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} {heroImg ? "Replace image" : "Upload image"}
                <input type="file" accept="image/*" className="hidden" disabled={heroBusy} onChange={(e) => uploadHero(e.target.files?.[0])} />
              </label>
            </div>
            <textarea className="w-full text-sm border border-input bg-background p-2 rounded-none" rows={2} placeholder="Short blurb (e.g. “Slow-steeped, served over ice”)" value={heroBlurb} onChange={(e) => setHeroBlurb(e.target.value)} data-testid={`cat-hero-blurb-${n.id}`} />
            <div className="flex gap-2 justify-end mt-2">
              <Button size="sm" variant="ghost" className="rounded-none h-7" onClick={() => setHeroFor(null)}>Cancel</Button>
              <Button size="sm" className="rounded-none h-7" disabled={heroBusy} onClick={() => saveHero(n.id)} data-testid={`cat-hero-save-${n.id}`}>Save hero</Button>
            </div>
          </div>
        )}
        {kids.map((k) => renderNode(k, depth + 1))}
      </div>
    );
  };

  const roots = childrenOf(nodes, null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="category-tree-manager">
        <DialogHeader>
          <DialogTitle className="font-display">Manage categories</DialogTitle>
          <DialogDescription>Build up to 3 levels — a Group, a Category and a Subcategory. Products are assigned to a branch.</DialogDescription>
        </DialogHeader>
        <div className="border border-border">
          {roots.length ? roots.map((n) => renderNode(n, 0)) : <div className="p-4 text-sm text-muted-foreground">No categories yet — add your first group below.</div>}
        </div>
        {addUnder === null ? (
          <div className="flex items-center gap-1" data-testid="cat-add-root-row">
            <Input autoFocus className="rounded-none h-8 text-sm flex-1" placeholder="New group name…" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") create(null); if (e.key === "Escape") setAddUnder(undefined); }} data-testid="cat-add-root-input" />
            <Button size="sm" className="rounded-none h-8" disabled={busy} onClick={() => create(null)} data-testid="cat-add-root-confirm">Add group</Button>
            <button className="text-muted-foreground p-1" onClick={() => setAddUnder(undefined)}><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <Button variant="outline" className="rounded-none gap-1 w-full" onClick={() => { setAddUnder(null); setNewName(""); }} data-testid="cat-add-group-btn"><Plus className="h-4 w-4" /> Add group (top level)</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
